import { useRef, useState, useCallback } from "react";
import * as THREE from "three";

/**
 * Compass yaw hook.
 * Tracks the camera's azimuth (yaw) in degrees so a compass UI can rotate
 * its needle. Updates are throttled to >1° deltas to avoid re-render storms.
 * Also surfaces the camera's world (x, z) position so a mini-map overlay
 * can plot the viewer's location on the floor plan.
 *
 * Usage:
 *   const { compassDeg, cameraPose, updateFromCamera } = useCompassYaw();
 *   // inside the tick loop:
 *   updateFromCamera(camera);
 *
 * @returns {object} { compassDeg, cameraPose: {x,z,yawDeg}, updateFromCamera }
 */
export default function useCompassYaw() {
  const [compassDeg, setCompassDeg] = useState(0);
  const [cameraPose, setCameraPose] = useState({ x: 0, z: 0, yawDeg: 0 });
  const lastYawRef = useRef(0);
  const lastPoseRef = useRef({ x: 0, z: 0, yawDeg: 0 });
  const fwdRef = useRef(new THREE.Vector3());

  const updateFromCamera = useCallback((camera) => {
    if (!camera) return;
    const fwd = fwdRef.current;
    camera.getWorldDirection(fwd);
    const yawRad = Math.atan2(fwd.x, fwd.z);
    const yawDeg = (yawRad * 180) / Math.PI;
    if (Math.abs(yawDeg - lastYawRef.current) > 1) {
      lastYawRef.current = yawDeg;
      setCompassDeg(yawDeg);
    }
    // Throttle mini-map updates to >0.25m moves or >2° yaw deltas so we
    // don't churn React for sub-pixel changes.
    const px = camera.position.x;
    const pz = camera.position.z;
    const last = lastPoseRef.current;
    if (
      Math.abs(px - last.x) > 0.25 ||
      Math.abs(pz - last.z) > 0.25 ||
      Math.abs(yawDeg - last.yawDeg) > 2
    ) {
      const next = { x: px, z: pz, yawDeg };
      lastPoseRef.current = next;
      setCameraPose(next);
    }
  }, []);

  return { compassDeg, cameraPose, updateFromCamera };
}
